/**
 * Client-side join of story_cost_summary events with calibration entries —
 * View A "Cost per Story Point" (RAISE-16869 D2/D3/D5/D7).
 *
 * All functions are pure — no side effects, no React, no `new Date()`.
 * `todayIso` is injected by the caller so "current month" is testable.
 *
 * KPI = ratio of sums (Σ cost_usd / Σ sp) — NOT the mean of per-story ratios.
 * Every joined story lands in exactly one bucket: included / excludedZeroSp
 * (matched but sp is null, <= 0, or non-finite) / unmatched (no calibration
 * entry for the normalized story key).
 */

import { aggregateByStory, type StoryCostEvent } from "./storyCostEvents";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CalibrationEntry {
  entryId: string;
  story: string;
  sp: number | null;
  createdAt: string; // "YYYY-MM-DD"
  projectSlug: string;
}

export interface StoryCostSp {
  ref: string;
  epic: string | null;
  costUsd: number;
  sp: number;
  month: string; // "YYYY-MM"
}

export interface MonthKpi {
  month: string;
  stories: number;
  totalCost: number;
  totalSp: number;
  costPerSp: number;
}

export interface EpicCostSp {
  epic: string;
  stories: number;
  totalCost: number;
  totalSp: number;
  costPerSp: number;
}

export interface CostPerSpResult {
  included: StoryCostSp[];
  excludedZeroSp: number;
  unmatched: number;
  currentMonth: MonthKpi | null;
  priorMonth: MonthKpi | null;
  epics: EpicCostSp[];
}

// ---------------------------------------------------------------------------
// monthOf / priorMonthOf
// ---------------------------------------------------------------------------

/** Extracts the "YYYY-MM" calendar-month bucket from an ISO date(-time) string. */
export function monthOf(dateIso: string): string {
  return dateIso.slice(0, 7);
}

/** "2026-08" -> "2026-07"; handles the year boundary ("2026-01" -> "2025-12"). */
export function priorMonthOf(month: string): string {
  const [yearStr, monthStr] = month.split("-");
  const year = parseInt(yearStr, 10);
  const mon = parseInt(monthStr, 10) - 1;
  if (mon === 0) {
    return `${year - 1}-12`;
  }
  return `${year}-${String(mon).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// indexCalibration
// ---------------------------------------------------------------------------

function normalizeStoryKey(story: string): string {
  return story.trim().toUpperCase();
}

/**
 * Index calibration entries by normalized story key (trim + uppercase).
 * On duplicate keys (possible across projects / re-syncs), the entry with
 * the latest `createdAt` wins.
 */
export function indexCalibration(
  entries: CalibrationEntry[],
): Map<string, CalibrationEntry> {
  const map = new Map<string, CalibrationEntry>();
  for (const entry of entries) {
    const key = normalizeStoryKey(entry.story);
    const existing = map.get(key);
    if (!existing || entry.createdAt > existing.createdAt) {
      map.set(key, entry);
    }
  }
  return map;
}

// ---------------------------------------------------------------------------
// joinCostWithSp
// ---------------------------------------------------------------------------

function isUsableSp(sp: number | null): sp is number {
  return sp !== null && Number.isFinite(sp) && sp > 0;
}

/**
 * Join story-level cost aggregates with calibration `sp`, then bucket into
 * current/prior calendar-month KPIs (relative to `todayIso`) and an
 * epic-level ratio-of-sums breakdown.
 *
 * Internally reuses `aggregateByStory` — dedupe across sessions is already
 * handled by the caller (useStoryCostEvents), so cost per story is summed
 * exactly once per ref before the join.
 */
export function joinCostWithSp(
  events: StoryCostEvent[],
  index: Map<string, CalibrationEntry>,
  todayIso: string,
): CostPerSpResult {
  const aggregates = aggregateByStory(events);

  // Per-ref latest effective date (date ?? createdAt) — mirrors the
  // useStoryCostEvents client-side filter convention — used for month
  // attribution when a story's `date` field is missing on every event.
  const lastEffectiveDate = new Map<string, string>();
  for (const e of events) {
    const effective = e.date ?? e.createdAt.slice(0, 10);
    const current = lastEffectiveDate.get(e.ref);
    if (!current || effective > current) {
      lastEffectiveDate.set(e.ref, effective);
    }
  }

  const included: StoryCostSp[] = [];
  let excludedZeroSp = 0;
  let unmatched = 0;

  for (const agg of aggregates) {
    const entry = index.get(normalizeStoryKey(agg.ref));
    if (!entry) {
      unmatched++;
      continue;
    }
    if (!isUsableSp(entry.sp)) {
      excludedZeroSp++;
      continue;
    }
    const effectiveDate = lastEffectiveDate.get(agg.ref) ?? todayIso;
    included.push({
      ref: agg.ref,
      epic: agg.epic,
      costUsd: agg.costUsd,
      sp: entry.sp,
      month: monthOf(effectiveDate),
    });
  }

  // Monthly buckets (ratio of sums per month)
  const monthTotals = new Map<
    string,
    { stories: number; totalCost: number; totalSp: number }
  >();
  for (const s of included) {
    const bucket = monthTotals.get(s.month) ?? {
      stories: 0,
      totalCost: 0,
      totalSp: 0,
    };
    bucket.stories += 1;
    bucket.totalCost += s.costUsd;
    bucket.totalSp += s.sp;
    monthTotals.set(s.month, bucket);
  }

  function monthKpi(month: string): MonthKpi | null {
    const bucket = monthTotals.get(month);
    if (!bucket) return null;
    return {
      month,
      stories: bucket.stories,
      totalCost: bucket.totalCost,
      totalSp: bucket.totalSp,
      costPerSp: bucket.totalCost / bucket.totalSp,
    };
  }

  const currentMonthLabel = monthOf(todayIso);
  const priorMonthLabel = priorMonthOf(currentMonthLabel);
  const currentMonth = monthKpi(currentMonthLabel);
  const priorMonth = monthKpi(priorMonthLabel);

  // Epic aggregation — only rendered when >= 1 included story has a
  // non-null epic (RAISE-16867 D6 precedent).
  let epics: EpicCostSp[] = [];
  if (included.some((s) => s.epic !== null)) {
    const epicTotals = new Map<
      string,
      { stories: number; totalCost: number; totalSp: number }
    >();
    for (const s of included) {
      const epicKey = s.epic ?? "unassigned";
      const bucket = epicTotals.get(epicKey) ?? {
        stories: 0,
        totalCost: 0,
        totalSp: 0,
      };
      bucket.stories += 1;
      bucket.totalCost += s.costUsd;
      bucket.totalSp += s.sp;
      epicTotals.set(epicKey, bucket);
    }
    epics = Array.from(epicTotals.entries()).map(([epic, bucket]) => ({
      epic,
      stories: bucket.stories,
      totalCost: bucket.totalCost,
      totalSp: bucket.totalSp,
      costPerSp: bucket.totalCost / bucket.totalSp,
    }));
  }

  return { included, excludedZeroSp, unmatched, currentMonth, priorMonth, epics };
}
